"use client";
import { useEffect } from "react";
import { usePathname } from "next/navigation";

// Lightweight, dependency-free replacement for the AOS library.
// Reveals [data-aos] elements when they scroll into view using CSS (see globals.css)
// plus one shared IntersectionObserver. Honors prefers-reduced-motion and per-element
// data-aos-duration. Mounted once globally in providers/Providers.tsx.
let observer: IntersectionObserver | null = null;

function getObserver(): IntersectionObserver | null {
  if (typeof window === "undefined" || !("IntersectionObserver" in window)) return null;
  if (observer) return observer;
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target as HTMLElement;
        const dur = el.getAttribute("data-aos-duration");
        if (dur) el.style.transitionDuration = `${dur}ms`;
        el.classList.add("aos-animate");
        observer?.unobserve(el); // once
      }
    },
    { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
  );
  return observer;
}

export function useAOS() {
  const pathname = usePathname();
  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    // Defer one frame so freshly-rendered nodes are in the DOM before we scan.
    const id = window.requestAnimationFrame(() => {
      const els = document.querySelectorAll<HTMLElement>(
        "[data-aos]:not(.aos-animate):not([data-aos-observed])"
      );
      const io = getObserver();
      els.forEach((el) => {
        el.setAttribute("data-aos-observed", "");
        if (reduce || !io) {
          el.classList.add("aos-animate"); // no motion / no IO support → reveal immediately
        } else {
          io.observe(el);
        }
      });
    });
    return () => window.cancelAnimationFrame(id);
  }, [pathname]);
}
